const OBJECT_ID = /^[a-f0-9]{24}$/i;
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;

export const shortDramaStorageKey = (userId, section) => userId ? `shortdrama:${userId}:${section}` : null;
export const validShortDramaId = (id) => OBJECT_ID.test(id || "");
export const shortDramaStatusLabel = (status) => ({
  draft: "Draft", angles_ready: "Pilihan angle siap", story_generated: "Cerita siap", storyboard_ready: "Storyboard siap", ready: "Siap dibuat",
  generating: "Memproses scene", processing: "Memproses scene", assembling: "Menyusun video",
  completed: "Selesai", failed: "Gagal", submission_unknown: "Submission perlu diperiksa",
  reconciliation_required: "Penyelesaian perlu diperiksa", partial_failed: "Sebagian scene gagal",
})[String(status || "draft").toLowerCase()] || status || "Draft";

export const shortDramaBusy = (project) => ["generating", "processing", "assembling", "submission_unknown", "reconciliation_required"].includes(String(project?.status || "").toLowerCase());
export const sameShortDramaVersion = (before, after) => Boolean(before && after && before.id === after.id &&
  before.revision === after.revision && before.updated_at === after.updated_at);
export const currentShortDramaPreview = (preview, project, target = null) => Boolean(preview && project &&
  preview.project_id === project.id && preview.revision === project.revision &&
  preview.updated_at === project.updated_at &&
  (preview.scope_scene_id || null) === (target || null) && preview.reliable === true &&
  preview.generation_ready === true && Number.isSafeInteger(preview.estimated_credits) && preview.estimated_credits >= 0);

export const readShortDramaReceipt = (storage, key) => {
  if (!key) return null;
  try {
    const receipt = JSON.parse(storage.getItem(key));
    if (!receipt || !UUID.test(receipt.client_request_id) || !OBJECT_ID.test(receipt.project_id) ||
      !Number.isSafeInteger(receipt.revision) || receipt.revision < 0 ||
      !["generate", "regenerate_scene"].includes(receipt.action) ||
      (receipt.action === "regenerate_scene" && (typeof receipt.scene_id !== "string" || !/^[a-zA-Z0-9_-]{1,80}$/.test(receipt.scene_id)))) return null;
    return { client_request_id: receipt.client_request_id, project_id: receipt.project_id, revision: receipt.revision,
      action: receipt.action, ...(receipt.action === "regenerate_scene" ? { scene_id: receipt.scene_id } : {}) };
  } catch { return null; }
};
export const persistShortDramaReceipt = (storage, key, receipt) => {
  if (!key) throw new Error("Sesi belum tersedia. Muat ulang sebelum melanjutkan.");
  try { storage.setItem(key, JSON.stringify(receipt)); }
  catch { throw new Error("Browser belum dapat menyimpan permintaan. Izinkan penyimpanan sesi sebelum Generate."); }
};

export const reorderShortDramaScenes = (scenes, index, direction) => {
  const other = index + direction;
  if (![1, -1].includes(direction) || index < 0 || index >= scenes.length || other < 0 || other >= scenes.length) return scenes;
  const next = scenes.map((scene) => ({ ...scene }));
  [next[index], next[other]] = [next[other], next[index]];
  return next.map((scene, position) => ({ ...scene, sequence: position + 1 }));
};

export const shortDramaScenePatch = (scene) => ({
  scene_id: scene.scene_id, location: scene.location, time_of_day: scene.time_of_day,
  action: scene.action, dialogue: (scene.dialogue || []).map((line) => ({ character_id: line.character_id, text: line.text })),
  narration: scene.narration, camera: scene.camera, visual_direction: scene.visual_direction,
  offer_presence: scene.offer_presence, soft_sell_role: scene.soft_sell_role, transition: scene.transition, voice_direction: scene.voice_direction,
  character_ids: Array.isArray(scene.character_ids) ? [...scene.character_ids] : [],
});

export const emptyShortDramaForm = () => ({
  title: "ShortDrama", offer: { name: "", type: "service", description: "", price_text: "", benefits: [], differentiators: [], desired_result: "", cta: "", contact: "" },
  audience: { description: "", age_range: "", gender: "", location: "", interests: [], problem: "", desire: "", buyer_context: "", language: "id" },
  objective: "soft_selling", style: "recommended", scene_count: 6, model_key: "", reference_asset_ids: [],
});

export const shortDramaFormPayload = (form) => ({
  title: String(form.title || "ShortDrama").trim(),
  offer: { name: String(form.offer.name || "").trim(), type: form.offer.type, description: String(form.offer.description || "").trim(),
    benefits: (form.offer.benefits || []).map((text) => String(text).trim()).filter(Boolean),
    differentiators: (form.offer.differentiators || []).map((text) => String(text).trim()).filter(Boolean),
    desired_result: String(form.offer.desired_result || "").trim(), cta: String(form.offer.cta || "").trim(),
    ...(form.offer.price_text?.trim() ? { price_text: form.offer.price_text.trim() } : {}),
    ...(form.offer.contact?.trim() ? { contact: form.offer.contact.trim() } : {}),
  },
  audience: { description: String(form.audience.description || "").trim(), age_range: String(form.audience.age_range || "").trim(),
    gender: String(form.audience.gender || "").trim(), location: String(form.audience.location || "").trim(),
    interests: (form.audience.interests || []).map((text) => String(text).trim()).filter(Boolean),
    problem: String(form.audience.problem || "").trim(), desire: String(form.audience.desire || "").trim(),
    buyer_context: String(form.audience.buyer_context || "").trim(), language: String(form.audience.language || "id").trim() },
  objective: form.objective, style: form.style, scene_count: form.scene_count,
  model_key: form.model_key || null, reference_asset_ids: [...(form.reference_asset_ids || [])],
});
